import React                       from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useIsAuthenticated }      from "@azure/msal-react";
import { useMsal }                 from "@azure/msal-react";

import ProtectedRoute     from "../components/common/ProtectedRoute";
import LoginPage          from "../pages/Auth/Login";
import PMODashboard       from "../pages/Dashboard/PMO";
import SCGDashboard       from "../pages/Dashboard/SCG";
import MACLeaderDashboard from "../pages/Dashboard/MACLeader";
import AdminDashboard     from "../pages/Dashboard/Admin";
import HRDashboard        from "../pages/Dashboard/HR";
import InvestmentLeague   from "../pages/InvestmentLeague";
import ResourcesPage      from "../pages/Resources";
import DossierPage        from "../pages/Dossier";
import { ROLES }          from "../constants/auth";
import AuthLoader from "../components/common/AuthLoader";

// Blank component shown while MSAL is processing the redirect response
// Prevents React Router from redirecting before MSAL handles the token
const MsalRedirectHandler = () => {
  return null;
};

const AppRoutes = () => {
  const isAuthenticated    = useIsAuthenticated();
  const { inProgress }     = useMsal();

  // While MSAL is handling the redirect - show nothing
  // This prevents the router from interfering with token processing
  const isMsalProcessing = inProgress !== "none";

  return (
    <Routes>
      {/* Root - MSAL lands here after redirect from Azure */}
      {/* Do not redirect while MSAL is still processing the token */}
      <Route
        path="/"
        element={
  isMsalProcessing
    ? <AuthLoader />          // ← smooth loader instead of blank
    : <Navigate to="/login" replace />
}
      />

      {/* Login page */}
      <Route
        path="/login"
        element={<LoginPage />}
      />

      {/* PMO only */}
      <Route
        path="/pmo/dashboard"
        element={
          <ProtectedRoute allowedRoles={[ROLES.PMO]}>
            <PMODashboard />
          </ProtectedRoute>
        }
      />

      {/* SCG only */}
      <Route
        path="/scg/dashboard"
        element={
          <ProtectedRoute allowedRoles={[ROLES.SCG]}>
            <SCGDashboard />
          </ProtectedRoute>
        }
      />

      {/* MAC Leader only */}
      <Route
        path="/mac/dashboard"
        element={
          <ProtectedRoute allowedRoles={[ROLES.MAC_LEADER]}>
            <MACLeaderDashboard />
          </ProtectedRoute>
        }
      />

      {/* Admin only */}
      <Route
        path="/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={[ROLES.ADMIN, ROLES.PMO]}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />

      {/* HR — read-only view of Position IDs, Associate Master, SCG Pool, Reports */}
      <Route
        path="/hr/dashboard"
        element={
          <ProtectedRoute allowedRoles={[ROLES.HR]}>
            <HRDashboard />
          </ProtectedRoute>
        }
      />

      {/* TAC (TSG) — same screens as HR, separate route */}
      <Route
        path="/tsg/dashboard"
        element={
          <ProtectedRoute allowedRoles={[ROLES.TAC]}>
            <HRDashboard />
          </ProtectedRoute>
        }
      />

      {/* CSG — same read-only screens as HR, separate route */}
      <Route
        path="/csg/dashboard"
        element={
          <ProtectedRoute allowedRoles={[ROLES.CSG]}>
            <HRDashboard />
          </ProtectedRoute>
        }
      />

      {/* Investment League — any authenticated user */}
      <Route
        path="/investment-league"
        element={
          <ProtectedRoute allowedRoles={[]}>
            <InvestmentLeague />
          </ProtectedRoute>
        }
      />
      <Route
        path="/investment-league/:subtab"
        element={<Navigate to="/investment-league" replace />}
      />

      {/* Data Assets — all authenticated roles */}
      <Route
        path="/resources"
        element={
          <ProtectedRoute allowedRoles={[]}>
            <ResourcesPage />
          </ProtectedRoute>
        }
      />

      {/* PSI360 Dossier — all authenticated roles */}
      <Route
        path="/dossier"
        element={
          <ProtectedRoute allowedRoles={[]}>
            <DossierPage />
          </ProtectedRoute>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
};

export default AppRoutes;